/**
 * Studio Profile View component - displays studio info on public profile
 */
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Building, Globe, Instagram, ExternalLink, DollarSign, 
  Clock, Camera, FileText, Check, X, Linkedin, Facebook 
} from "lucide-react";
import { 
  GENRES, SHOOTING_STYLES, HIREABLE_ROLES, PAYMENT_TIMING_OPTIONS, 
  RATE_TYPES, getLabel 
} from "@/lib/profileOptions";
import { VideoPortfolioDisplay } from "./VideoPortfolioSection";

export function StudioProfileView({ profile, studioProfile }) {
  const sp = studioProfile || {};
  
  const formatRate = (rate) => {
    const type = getLabel(RATE_TYPES, rate.rate_type);
    if (rate.fixed_rate) {
      return `$${rate.fixed_rate} ${type}`;
    }
    if (rate.min_rate && rate.max_rate) {
      return `$${rate.min_rate} - $${rate.max_rate} ${type}`;
    }
    if (rate.min_rate) {
      return `$${rate.min_rate}+ ${type}`;
    }
    if (rate.max_rate) {
      return `Up to $${rate.max_rate} ${type}`;
    }
    return "Negotiable";
  };

  return (
    <div className="space-y-6" data-testid="studio-profile-section">
      {/* Studio Header */}
      <Card className="bg-white/[0.03] border-white/5 p-8">
        <div className="flex items-start gap-6">
          {sp.studio_logo ? (
            <Avatar className="w-24 h-24 border-2 border-white/10 rounded-lg">
              <AvatarImage src={sp.studio_logo} className="object-contain" />
              <AvatarFallback className="bg-white/[0.05] text-white text-2xl rounded-lg">
                {sp.studio_name ? sp.studio_name.charAt(0).toUpperCase() : "S"}
              </AvatarFallback>
            </Avatar>
          ) : (
            <div className="w-24 h-24 bg-white/[0.05] border-2 border-white/10 rounded-lg flex items-center justify-center">
              <Building className="w-10 h-10 text-white/40" />
            </div>
          )}
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Building className="w-5 h-5 text-white/60" />
              <span className="text-white/60 text-sm uppercase tracking-wider">Studio</span>
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {sp.studio_name || profile.name}
            </h2>
            
            <div className="flex flex-wrap items-center gap-4 text-white/60 text-sm">
              {sp.years_in_business && (
                <span>{sp.years_in_business} years in business</span>
              )}
              {profile.location?.city && (
                <span>{profile.location.city}, {profile.location.state}</span>
              )}
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4 mt-4">
              {sp.website_url && (
                <a 
                  href={sp.website_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1 text-white/60 hover:text-white transition-colors"
                >
                  <Globe className="w-4 h-4" />
                  <span className="text-sm">Website</span>
                </a>
              )}
              {sp.instagram_handle && (
                <a 
                  href={`https://instagram.com/${sp.instagram_handle.replace('@', '')}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-white/60 hover:text-white transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                  <span className="text-sm">{sp.instagram_handle}</span>
                </a>
              )}
              {sp.facebook_url && (
                <a 
                  href={sp.facebook_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-white/60 hover:text-white transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {sp.linkedin_url && (
                <a 
                  href={sp.linkedin_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-white/60 hover:text-white transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* What They Hire For */}
      {sp.hiring_genres && sp.hiring_genres.length > 0 && (
        <Card className="bg-white/[0.03] border-white/5 p-8">
          <h3 className="text-lg font-semibold mb-4">
            <Camera className="w-5 h-5 inline mr-2" />
            Hires For
          </h3>
          <div className="flex flex-wrap gap-2">
            {sp.hiring_genres.map((genre) => (
              <Badge 
                key={genre} 
                className="bg-coral-dim text-coral border-blue-500/30 rounded-sm px-3 py-1"
              >
                {getLabel(GENRES, genre)}
              </Badge>
            ))}
          </div>
          
          {/* Shooting Styles */}
          {sp.shooting_styles && sp.shooting_styles.length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/10">
              <h4 className="text-white/60 text-sm uppercase tracking-wider mb-3">Studio Aesthetic</h4>
              <div className="flex flex-wrap gap-2">
                {sp.shooting_styles.map((style) => (
                  <Badge 
                    key={style} 
                    className="bg-white/10 text-white border-white/20 rounded-sm px-3 py-1"
                  >
                    {getLabel(SHOOTING_STYLES, style)}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Pay Rates */}
      {sp.pay_rates && sp.pay_rates.length > 0 && (
        <Card className="bg-white/[0.03] border-white/5 p-8">
          <h3 className="text-lg font-semibold mb-4">
            <DollarSign className="w-5 h-5 inline mr-2" />
            Typical Pay Rates
          </h3>
          <div className="space-y-3">
            {sp.pay_rates.map((rate, index) => (
              <div 
                key={index} 
                className="flex items-center justify-between p-4 bg-white/[0.05] rounded-sm"
              >
                <span className="font-medium">{getLabel(HIREABLE_ROLES, rate.role)}</span>
                <span className="text-coral font-bold">{formatRate(rate)}</span>
              </div>
            ))}
          </div>
          
          {/* Payment Timing */}
          {sp.payment_timing && (
            <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2 text-white/60">
              <Clock className="w-4 h-4" />
              <span>Payment: {getLabel(PAYMENT_TIMING_OPTIONS, sp.payment_timing)}</span>
            </div>
          )}
        </Card>
      )}

      {/* Portfolio Usage Policy */}
      {sp.portfolio_usage && (
        <Card className="bg-white/[0.03] border-white/5 p-8">
          <h3 className="text-lg font-semibold mb-4">
            <FileText className="w-5 h-5 inline mr-2" />
            Portfolio Usage Policy
          </h3>
          
          <div className="p-4 bg-white/[0.05] rounded-sm mb-4">
            {sp.portfolio_usage.allowed === "yes" && (
              <div className="flex items-center gap-2 text-coral">
                <Check className="w-5 h-5" />
                <span className="font-medium">Team members can use images freely</span>
              </div>
            )}
            {sp.portfolio_usage.allowed === "no" && (
              <div className="flex items-center gap-2 text-red-400">
                <X className="w-5 h-5" />
                <span className="font-medium">Images cannot be used in personal portfolios</span>
              </div>
            )}
            {sp.portfolio_usage.allowed === "with_restrictions" && (
              <div className="flex items-center gap-2 text-yellow-400">
                <FileText className="w-5 h-5" />
                <span className="font-medium">Images can be used with restrictions</span>
              </div>
            )}
          </div>

          {/* Show restrictions if applicable */}
          {sp.portfolio_usage.allowed === "with_restrictions" && (
            <div className="space-y-2 text-sm">
              {sp.portfolio_usage.after_weeks && (
                <div className="flex items-center gap-2 text-white/70">
                  <Clock className="w-4 h-4 text-white/40" />
                  <span>After {sp.portfolio_usage.after_weeks} week waiting period</span>
                </div>
              )}
              {sp.portfolio_usage.must_credit_studio && (
                <div className="flex items-center gap-2 text-white/70">
                  <Check className="w-4 h-4 text-white/40" />
                  <span>Must credit studio</span>
                </div>
              )}
              {sp.portfolio_usage.with_studio_watermark && (
                <div className="flex items-center gap-2 text-white/70">
                  <Check className="w-4 h-4 text-white/40" />
                  <span>Must include studio watermark</span>
                </div>
              )}
              {sp.portfolio_usage.portfolio_only && (
                <div className="flex items-center gap-2 text-white/70">
                  <Check className="w-4 h-4 text-white/40" />
                  <span>Portfolio use only (no marketing/ads)</span>
                </div>
              )}
              {sp.portfolio_usage.social_media_only && (
                <div className="flex items-center gap-2 text-white/70">
                  <Check className="w-4 h-4 text-white/40" />
                  <span>Social media only</span>
                </div>
              )}
              {sp.portfolio_usage.must_not_tag_client && (
                <div className="flex items-center gap-2 text-white/70">
                  <Check className="w-4 h-4 text-white/40" />
                  <span>Must not tag client</span>
                </div>
              )}
              {sp.portfolio_usage.additional_terms && (
                <div className="mt-3 p-3 bg-[#0a0a0a] rounded-sm text-white/60 italic">
                  "{sp.portfolio_usage.additional_terms}"
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Video Portfolio */}
      {sp.portfolio_videos && sp.portfolio_videos.length > 0 && (
        <Card className="bg-white/[0.03] border-white/5 p-6">
          <VideoPortfolioDisplay videos={sp.portfolio_videos} />
        </Card>
      )}
    </div>
  );
}

export default StudioProfileView;
